/* ─── OyoBooking API Client ────────────────────────────────────
   Defaults to same-origin /api/v1 so the published ASP.NET app can
   serve both the dashboard and API from one live host. Set VITE_API_URL
   only when the API is hosted on a different origin.
   ─────────────────────────────────────────────────────────────── */

const configuredApiBase = (
  import.meta as { env?: { VITE_API_URL?: string } }
).env?.VITE_API_URL?.trim();

// Defensive: on HTTPS pages, ignore VITE_API_URL if it starts with http://
// (avoids Mixed Content blocks). Use same-origin /api instead.
const isHttps = typeof window !== "undefined" && window.location.protocol === "https:";
const safeApiBase = isHttps && configuredApiBase?.startsWith("http://")
  ? ""
  : configuredApiBase;

export const API_BASE = safeApiBase ? safeApiBase.replace(/\/$/, "") : "";

const V1 = `${API_BASE}/api/v1`;

const TOKEN_KEY = "oyo_token";
const REFRESH_KEY = "oyo_refresh";

let refreshPromise: Promise<{ accessToken: string; refreshToken: string } | null> | null = null;

export class AuthExpiredError extends Error {
  constructor(message = "Your session has expired. Please sign in again to continue.") {
    super(message);
    this.name = "AuthExpiredError";
  }
}

export function isAuthExpiredError(error: unknown): error is AuthExpiredError {
  return error instanceof AuthExpiredError || (error instanceof Error && error.name === "AuthExpiredError");
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string) {
  localStorage.setItem(TOKEN_KEY, t);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}
export function setRefreshToken(t: string) {
  localStorage.setItem(REFRESH_KEY, t);
}

async function refreshStoredAuth(): Promise<boolean> {
  const currentRefresh = getRefreshToken();
  if (!currentRefresh) return false;

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const currentAccess = getToken();
        const refreshRes = await fetch(`${V1}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accessToken: currentAccess, refreshToken: currentRefresh }),
        });
        if (refreshRes.ok) {
          const data = (await refreshRes.json()) as {
            accessToken: string;
            refreshToken: string;
          };
          setToken(data.accessToken);
          if (data.refreshToken) setRefreshToken(data.refreshToken);
          return data;
        }
      } catch {
        /* fall through */
      }
      return null;
    })();
  }

  const data = await refreshPromise;
  refreshPromise = null;
  return !!data;
}

export function resolveMediaUrl(url?: string | null): string {
  if (!url) return "";
  if (/^(blob:|data:)/i.test(url)) return url;

  // Stored upload URLs may contain old backend hosts (e.g.
  // https://oyo-booking-ten.vercel.app/uploads/...,
  // http://aftsolutions-003-site11.jtempurl.com/uploads/...).
  // On HTTPS production we rewrite the host to the API directly
  // so images load from api.oyobooking.ng without relying on a proxy.
  // On HTTP localhost we keep the absolute URL so the dev server
  // or remote backend can serve the file directly.
  const API_HOST = API_BASE || "https://api.oyobooking.ng";
  const isHttps = typeof window !== "undefined" && window.location.protocol === "https:";

  if (/^https?:/i.test(url)) {
    try {
      const parsed = new URL(url);
      const path = parsed.pathname + parsed.search;
      if (path.startsWith("/uploads/") || path.startsWith("/upload/")) {
        return isHttps ? `${API_HOST}${path}` : url;
      }
      return url; // external URL (CDN, S3, etc.)
    } catch {
      return url;
    }
  }

  const normalizedPath = url.startsWith("/") ? url : `/${url}`;
  if (isHttps && (normalizedPath.startsWith("/uploads/") || normalizedPath.startsWith("/upload/"))) {
    return `${API_HOST}${normalizedPath}`;
  }
  if (API_BASE) return `${API_BASE}${normalizedPath}`;
  if (typeof window !== "undefined") return `${window.location.origin}${normalizedPath}`;
  return normalizedPath;
}

/** Decode a JWT payload without verifying the signature (client-side only). */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

/** Extract the role string from a decoded JWT payload (ASP.NET Core convention).
    ASP.NET Identity can emit the role claim as a single string or an array. */
export function jwtRole(token: string): string | null {
  const p = decodeJwtPayload(token);
  if (!p) return null;
  const raw = p["role"] ?? p["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
  if (Array.isArray(raw)) return raw[0] as string | null;
  return (raw as string) ?? null;
}

const CLAIM_UID = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier";
const CLAIM_EMAIL = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress";

export function jwtUserId(token?: string | null): string | null {
  const t = token ?? getToken();
  if (!t) return null;
  const p = decodeJwtPayload(t);
  return (p?.[CLAIM_UID] as string) ?? (p?.["sub"] as string) ?? null;
}

export function jwtEmail(token?: string | null): string | null {
  const t = token ?? getToken();
  if (!t) return null;
  const p = decodeJwtPayload(t);
  return (p?.[CLAIM_EMAIL] as string) ?? (p?.["email"] as string) ?? null;
}

export function jwtDisplayName(token?: string | null): string | null {
  const t = token ?? getToken();
  if (!t) return null;
  const p = decodeJwtPayload(t);
  const given = (p?.["given_name"] as string) ?? "";
  const family = (p?.["family_name"] as string) ?? "";
  return [given, family].filter(Boolean).join(" ") || null;
}

export function isTokenValid(token?: string | null): boolean {
  const t = token ?? getToken();
  if (!t) return false;
  const p = decodeJwtPayload(t);
  if (!p) return false;
  const exp = p.exp as number | undefined;
  return !exp || exp * 1000 > Date.now();
}

export async function ensureFreshAuth(): Promise<boolean> {
  const token = getToken();
  if (token && isTokenValid(token)) return true;

  const refreshed = await refreshStoredAuth();
  if (!refreshed) clearToken();
  return refreshed;
}

export type MappedRole =
  | "super_admin"
  | "venue_manager"
  | "hotel_owner"
  | "event_centre_owner"
  | "open_space_owner"
  | "resort_owner"
  | "hotel_event_owner"
  | "apartment_owner"
  | "conference_hall_owner"
  | "moderator"
  | null;

function mapJwtRole(raw: string | null): MappedRole {
  if (raw === "SuperAdmin") return "super_admin";
  if (raw === "VenueManager") return "venue_manager";
  if (raw === "HotelOwner") return "hotel_owner";
  if (raw === "EventCentreOwner") return "event_centre_owner";
  if (raw === "OpenSpaceOwner") return "open_space_owner";
  if (raw === "ResortOwner") return "resort_owner";
  if (raw === "HotelEventOwner") return "hotel_event_owner";
  if (raw === "ApartmentOwner") return "apartment_owner";
  if (raw === "ConferenceHallOwner") return "conference_hall_owner";
  if (raw === "Moderator") return "moderator";
  return null;
}

/**
 * Single source of truth for route guards and auth bootstrap.
 * Reads ONLY from the stored JWT — no oyo_user object.
 */
export function getStoredAuth(): {
  id: string;
  email: string;
  name: string;
  role: MappedRole;
} | null {
  const token = getToken();
  if (!token || !isTokenValid(token)) return null;
  const id = jwtUserId(token);
  const email = jwtEmail(token);
  if (!id || !email) return null;
  return {
    id,
    email,
    name: jwtDisplayName(token) ?? email,
    role: mapJwtRole(jwtRole(token)),
  };
}

/* ── Shared fetch helper ──────────────────────────────────────── */

interface FetchOpts {
  method?: string;
  body?: unknown;
  auth?: boolean;
}

/** Return a user-friendly message for common HTTP status codes or network failures. */
function friendlyError(status: number, _backendMsg?: string): string {
  if (_backendMsg) {
    try {
      const json = JSON.parse(_backendMsg);
      const raw =
        json.detail ??
        json.errorDetail ??
        json.message ??
        json.Message ??
        json.error ??
        json.title;
      if (raw && typeof raw === "string") {
        // Custom user-friendly overrides for known backend errors
        if (raw.includes("already have an active booking")) {
          return "You already have an active booking for this venue during these dates. Check your Dashboard to manage it.";
        }
        return raw;
      }
      if (json.errors && typeof json.errors === "object") {
        const keys = Object.keys(json.errors);
        if (keys.length > 0) {
          const val = json.errors[keys[0]];
          if (Array.isArray(val) && typeof val[0] === "string") {
            return val[0];
          } else if (typeof val === "string") {
            return val;
          }
        }
      }
    } catch {
      if (typeof _backendMsg === "string" && _backendMsg.trim()) {
        const clean = _backendMsg.trim();
        if (!clean.includes("<!DOCTYPE html>") && !clean.includes("<html>")) {
          return clean;
        }
      }
    }
  }

  switch (status) {
    case 0:
    case -1:
      return "Please check your internet connection and try again.";
    case 400:
      return "Something went wrong with your request. Please check your input and try again.";
    case 401:
      return "Your session has expired. Please sign in again.";
    case 403:
      return "You do not have permission to perform this action.";
    case 404:
      return "The requested item could not be found.";
    case 409:
      return "This action conflicts with an existing record. Please try again.";
    case 422:
      return "Some fields are invalid. Please review your input and try again.";
    case 429:
      return "Too many requests. Please wait a moment and try again.";
    case 500:
    case 502:
    case 503:
    case 504:
      return "Something went wrong on our end. Please try again shortly.";
    default:
      return "Something went wrong. Please check your internet connection and try again.";
  }
}

async function apiFetch<T>(path: string, opts: FetchOpts = {}, _retried = false): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (opts.auth !== false) {
    const tok = getToken();
    if (tok) headers["Authorization"] = `Bearer ${tok}`;
  }

  let res: Response;
  try {
    res = await fetch(`${V1}${path}`, {
      method: opts.method ?? "GET",
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      cache: "no-cache",
    });
  } catch (err) {
    // Network / DNS / offline errors
    console.error(`[API] ${opts.method ?? "GET"} ${V1}${path} → Network error`, err);
    throw new Error(friendlyError(0));
  }

  // Silent token refresh on 401 — retry the original request once with the new token
  if (res.status === 401 && !_retried && opts.auth !== false) {
    const refreshed = await refreshStoredAuth();
    if (refreshed) {
      return apiFetch<T>(path, opts, true);
    }
    // Refresh failed — session expired, let route guards handle redirect
    clearToken();
  }

  if (!res.ok) {
    if (res.status === 401 && opts.auth !== false) {
      clearToken();
      throw new AuthExpiredError();
    }
    const text = await res.text().catch(() => res.statusText);
    console.error(`[API] ${opts.method ?? "GET"} ${V1}${path} → HTTP ${res.status}`, text);
    throw new Error(friendlyError(res.status, text));
  }

  // 204 No Content — return undefined so callers don't crash parsing empty body
  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as unknown as Promise<T>;
  }

  return res.json() as Promise<T>;
}

/* ── File download helper (blob → auto-download) ────────────── */

async function apiDownload(path: string, filename: string): Promise<void> {
  const tok = getToken();
  const res = await fetch(`${V1}${path}`, {
    headers: tok ? { Authorization: `Bearer ${tok}` } : {},
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/* ── File upload helper (multipart/form-data) ───────────────── */

async function uploadFile(path: string, formData: FormData, _retried = false): Promise<{ url: string }> {
  const headers: Record<string, string> = {};
  const tok = getToken();
  if (tok) headers["Authorization"] = `Bearer ${tok}`;

  const res = await fetch(`${V1}${path}`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (res.status === 401 && !_retried) {
    const refreshed = await refreshStoredAuth();
    if (refreshed) {
      return uploadFile(path, formData, true);
    }

    clearToken();
    throw new AuthExpiredError();
  }

  if (!res.ok) {
    if (res.status === 401) {
      clearToken();
      throw new AuthExpiredError();
    }
    const text = await res.text().catch(() => res.statusText);
    console.error(`[UPLOAD] POST ${V1}${path} → HTTP ${res.status}`, text);
    try {
      const json = JSON.parse(text);
      const msg = json.detail ?? json.message ?? json.Message ?? json.error ?? json.title;
      if (msg)
        throw new Error(
          `HTTP ${res.status}: ${typeof msg === "string" ? msg : JSON.stringify(msg)}`,
        );
    } catch {
      /* noop */
    }
    throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<{ url: string }>;
}

export const uploadApi = {
  image: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return uploadFile("/upload", fd).then((res) => ({ url: resolveMediaUrl(res.url) }));
  },
  /** Anonymous upload for pre-registration document images (CAC/NIN). */
  publicImage: (file: File) => {
    const fd = new FormData();
    fd.append("File", file); // capital F matches UploadImageCommand property
    return uploadFile("/upload/image", fd).then((res) => ({ url: resolveMediaUrl(res.url) }));
  },
  uploadImages: async (files: File[]): Promise<UploadResponse> => {
    const uploaded = await Promise.all(files.map((file) => uploadApi.image(file)));
    return { urls: uploaded.map((item) => item.url) };
  },
};

/* ── Gallery ────────────────────────────────────────────────── */

export interface GalleryImageResponse {
  id: string;
  imageUrl: string;
  caption?: string;
}

export const galleryApi = {
  add: (venueId: string, data: { imageUrl: string; caption?: string }) =>
    apiFetch<GalleryImageResponse>(`/venues/${venueId}/gallery`, { method: "POST", body: data }),

  delete: (venueId: string, imageId: string) =>
    apiFetch<void>(`/venues/${venueId}/gallery/${imageId}`, { method: "DELETE" }),
};

/* ── Auth ─────────────────────────────────────────────────────── */

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  dateOfBirth: string;
  location: string;
  role?: string;
  businessName?: string;
  businessType?: string;
  cacNumber?: string;
  cacImage?: string;
  ninNumber?: string;
  ninImage?: string;
  hasEventCentre?: boolean;
  numberOfUnits?: number;
  furnished?: boolean;
  rentalType?: string;
  seatingCapacity?: number;
  hasAvEquipment?: boolean;
  parkingSpaces?: number;
}

export interface AuthUserDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  location: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiration: string;
  user: AuthUserDto;
}

export interface RegisterResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export const authApi = {
  /* BACKEND: POST /api/v1/auth/login — Accepts { email, password } */
  login: (data: LoginRequest) =>
    apiFetch<LoginResponse>("/auth/login", { method: "POST", body: data, auth: false }),

  /* BACKEND: POST /api/v1/auth/register — Accepts full user registration payload */
  register: (data: RegisterRequest) =>
    apiFetch<RegisterResponse>("/auth/register", { method: "POST", body: data, auth: false }),

  /* BACKEND: POST /api/v1/auth/refresh — Accepts { accessToken, refreshToken } */
  refresh: () => {
    const accessToken = getToken();
    const refreshToken = getRefreshToken();
    if (!accessToken || !refreshToken) return Promise.reject(new Error("No tokens to refresh"));
    return apiFetch<LoginResponse>("/auth/refresh", {
      method: "POST",
      body: { accessToken, refreshToken },
      auth: false,
    });
  },

  /* BACKEND: POST /api/v1/auth/forgot-password — Accepts { email } */
  forgotPassword: (email: string) =>
    apiFetch<void>("/auth/forgot-password", { method: "POST", body: { email }, auth: false }),

  /* BACKEND: POST /api/v1/auth/verify-reset-otp — Accepts { email, code } */
  verifyResetOtp: (data: { email: string; code: string }) =>
    apiFetch<void>("/auth/verify-reset-otp", { method: "POST", body: data, auth: false }),

  /* BACKEND: POST /api/v1/auth/reset-password — Accepts { email, code, newPassword } */
  resetPassword: (data: { email: string; code: string; newPassword: string }) =>
    apiFetch<void>("/auth/reset-password", { method: "POST", body: data, auth: false }),
};

/* ── Venues ─────────────────────────────────────────────────────
   BACKEND ENDPOINTS REQUIRED:
   GET  /api/v1/venues            → List all (supports ?type, ?location, ?minPrice, etc.)
   GET  /api/v1/venues/:id        → Detail view (public, no auth)
   POST /api/v1/venues            → Create listing (auth required)
   PUT  /api/v1/venues/:id        → Update (auth required)
   GET  /api/v1/venues?managerId= → Host's own venues (auth required)
   ─────────────────────────────────────────────────────────────── */

export interface VenueResponse {
  id: string;
  name: string;
  type: string;
  location: string;
  address?: string;
  description?: string;
  fullDescription?: string;
  rating: number;
  reviewsCount: number;
  bookingCount: number;
  basePrice: number;
  seasonalPrice?: number;
  status: string;
  isAvailable: boolean;
  heroImage: string;
  gallery?: { id: string; imageUrl: string; caption?: string }[];
  highlights: { name: string; icon?: string }[];
  moderationStatus?: string | null;
  capacity?: number;
  maxCapacity?: number;
  bedrooms?: number;
  latitude?: number | string;
  longitude?: number | string;
  roomTypes?: {
    id: string;
    name: string;
    pricePerDay: number;
    capacity: number;
    quantity: number;
    isActive: boolean;
    description?: string;
    images?: RoomTypeImageResponse[];
    amenities?: string[];
  }[];
}

export interface VenueDetailResponse extends VenueResponse {
  address: string;
  state: string;
  gallery: { id: string; imageUrl: string; caption?: string }[];
  roomTypes: {
    id: string;
    name: string;
    pricePerDay: number;
    capacity: number;
    quantity: number;
    isActive: boolean;
    description?: string;
    images: RoomTypeImageResponse[];
    amenities: string[];
  }[];
}

export interface PaginationMeta {
  currentPage: number;
  from: number;
  lastPage: number;
  path: string;
  perPage: number;
  to: number;
  total: number;
}
export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginationMeta;
}

export interface VenueListParams {
  type?: string;
  location?: string;
  minPrice?: number;
  maxPrice?: number;
  isAvailable?: boolean;
  managerId?: string;
  moderationStatus?: "draft" | "pending" | "approved" | "rejected" | "all";
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateVenueRequest {
  name: string;
  type: string;
  description?: string;
  address: string;
  location: string;
  heroImage?: string;
  gallery?: string[];
  basePrice: number;
  seasonalPrice?: number;
  latitude?: number;
  longitude?: number;
  highlights?: { name: string; icon?: string }[];
  roomTypes?: {
    name: string;
    pricePerDay: number;
    capacity: number;
    quantity?: number;
    description?: string;
    images?: string[];
  }[];
}

export const venuesApi = {
  /* BACKEND: GET /api/v1/venues — Returns paginated list { data: [...], meta: {...} } */
  list: (params?: VenueListParams, opts?: { auth?: boolean }) => {
    const qs = params
      ? "?" +
        new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : "";
    return apiFetch<PaginatedResponse<VenueResponse>>(`/venues${qs}`, {
      auth: opts?.auth ?? false,
    });
  },

  /* BACKEND: GET /api/v1/venues?moderationStatus=all — Admin/moderator moderation queue */
  moderationList: (params?: VenueListParams) =>
    venuesApi.list({ moderationStatus: "all", ...params }, { auth: true }),

  /* BACKEND: GET /api/v1/venues?managerId=:id&pageSize=50 — Host's listings */
  myVenues: (managerId: string) =>
    apiFetch<PaginatedResponse<VenueResponse>>(
      `/venues?managerId=${encodeURIComponent(managerId)}&pageSize=50`,
    ),

  /* BACKEND: GET /api/v1/venues/:id — Public detail with gallery + roomTypes */
  getById: (id: string) => apiFetch<VenueDetailResponse>(`/venues/${id}`, { auth: false }),

  /* BACKEND: POST /api/v1/venues — Body: CreateVenueRequest (see interface above) */
  create: (data: CreateVenueRequest) =>
    apiFetch<{ id: string; name: string; type: string; status: string; isAvailable: boolean }>(
      "/venues",
      { method: "POST", body: data },
    ),

  update: (id: string, data: Partial<CreateVenueRequest> & { isAvailable?: boolean }) =>
    apiFetch<{ id: string; name: string; type: string; status: string; isAvailable: boolean }>(
      `/venues/${id}`,
      { method: "PUT", body: data },
    ),

  approve: (id: string, moderatorNote?: string) =>
    apiFetch<{
      venueId: string;
      name: string;
      moderationStatus: string;
      moderatedAt?: string;
      moderatorNote?: string;
    }>(`/venues/${id}/approve`, { method: "POST", body: { moderatorNote } }),

  reject: (id: string, moderatorNote?: string) =>
    apiFetch<{
      venueId: string;
      name: string;
      moderationStatus: string;
      moderatedAt?: string;
      moderatorNote?: string;
    }>(`/venues/${id}/reject`, { method: "POST", body: { venueId: id, moderatorNote } }),

  delete: (id: string) => apiFetch<{ deleted: boolean }>(`/venues/${id}`, { method: "DELETE" }),

  /* BACKEND: GET /api/v1/venues/:id/availability — Availability calendar (public) */
  availability: (id: string, params?: { startDate?: string; endDate?: string; roomTypeId?: string }) => {
    const qs = params
      ? "?" +
        new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : "";
    return apiFetch<{ date: string; status: string; roomTypeId: string | null }[]>(`/venues/${id}/availability${qs}`, { auth: false });
  },

  /* BACKEND: POST /api/v1/venues/:id/block-dates — Block dates (manager) */
  blockDates: (id: string, data: { dates: string[]; roomTypeId?: string; reason?: string }) =>
    apiFetch<{
      venueId: string;
      blockedCount: number;
      dates: string[];
      message: string;
    }>(`/venues/${id}/block-dates`, { method: "POST", body: data }),

  /* BACKEND: DELETE /api/v1/venues/:id/block-dates — Unblock dates (manager) */
  unblockDates: (id: string, data: { dates: string[]; roomTypeId?: string }) =>
    apiFetch<{
      venueId: string;
      unblockedCount: number;
      dates: string[];
      message: string;
    }>(`/venues/${id}/block-dates`, { method: "DELETE", body: data }),
};

/* ── Events ─────────────────────────────────────────────────────
   BACKEND ENDPOINTS REQUIRED:
   GET  /api/v1/events              → List events
   GET  /api/v1/events/:id          → Event detail
   POST /api/v1/events              → Create event
   PUT  /api/v1/events/:id          → Update event
   DELETE /api/v1/events/:id        → Delete event
   POST /api/v1/events/:id/register → Register for event
   ─────────────────────────────────────────────────────────────── */

export interface EventTierResponse {
  id: string;
  name: string;
  tierType: string;
  price: number;
  capacity: number;
  sold: number;
  status: string;
}

export interface EventResponse {
  id: string;
  eventRef: string;
  name: string;
  organizer: string;
  date: string;
  venueName: string;
  image?: string;
  status: string;
  totalRegistrations: number;
}

export interface EventDetailResponse extends EventResponse {
  description?: string;
  time?: string;
  venueId?: string;
  tiers: EventTierResponse[];
}

export interface CreateEventRequest {
  name: string;
  organizer: string;
  description?: string;
  date: string;
  time?: string;
  venueId?: string;
  venueName?: string;
  image?: string;
  status?: string;
  tiers?: { id?: string; name: string; tierType: string; price: number; capacity: number }[];
}

export interface TicketDto {
  id: string;
  ticketRef: string;
  ticketCode: string;
  tierName: string;
  tierType: string;
  status: string;
  qrCodeImage: string | null;
}

export interface MyEventRegistrationResponse {
  id: string;
  eventId: string;
  eventName: string;
  eventDate: string;
  ticketTierName: string;
  quantity: number;
  totalAmount: number;
  paymentStatus: string;
  ticketCode: string;
  createdAt: string;
}

export const eventsApi = {
  /* BACKEND: GET /api/v1/events */
  list: (params?: { status?: string; venueId?: string; managerId?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.venueId) qs.set("venueId", params.venueId);
    if (params?.managerId) qs.set("managerId", params.managerId);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("pageSize", String(params.pageSize));
    const query = qs.toString();
    return apiFetch<PaginatedResponse<EventResponse>>(`/events${query ? "?" + query : ""}`, { auth: false });
  },

  /* BACKEND: GET /api/v1/events/:id */
  getById: (id: string) => apiFetch<EventDetailResponse>(`/events/${id}`, { auth: false }),

  /* BACKEND: GET /api/v1/events/my-registrations */
  getMyRegistrations: () => apiFetch<MyEventRegistrationResponse[]>("/events/my-registrations"),

  /* BACKEND: POST /api/v1/events */
  create: (data: CreateEventRequest) =>
    apiFetch<EventResponse>("/events", { method: "POST", body: data }),

  /* BACKEND: PUT /api/v1/events/:id */
  update: (id: string, data: Partial<CreateEventRequest>) =>
    apiFetch<EventResponse>(`/events/${id}`, { method: "PUT", body: data }),

  /* BACKEND: DELETE /api/v1/events/:id */
  delete: (id: string) => apiFetch<{ deleted: boolean }>(`/events/${id}`, { method: "DELETE" }),

  /* BACKEND: POST /api/v1/events/registrations/:id/cancel */
  cancelRegistration: (id: string, reason?: string) =>
    apiFetch<{ registrationId: string; status: string; paymentStatus: string; refundedAmount: number | null }>(
      `/events/registrations/${id}/cancel`,
      { method: "POST", body: { reason } }
    ),

  /* BACKEND: POST /api/v1/events/:id/register */
  register: (id: string, data: {
    name: string;
    email: string;
    phone?: string;
    tierId?: string;
    quantity?: number;
    paymentMethod?: "wallet" | "bank_transfer";
  }) =>
    apiFetch<{
      id: string;
      ticketCode: string;
      eventId: string;
      eventName: string;
      tierName: string;
      tierType: string;
      quantity: number;
      totalAmount: number;
      paymentStatus: string;
      paymentInstruction: string | null;
      dedicatedBank: { accountNumber: string; accountName: string; bankName: string } | null;
      tickets: TicketDto[] | null;
    }>(`/events/${id}/register`, {
      method: "POST",
      body: {
        attendeeName: data.name,
        attendeeEmail: data.email,
        phone: data.phone,
        ticketTierId: data.tierId,
        quantity: data.quantity,
        paymentMethod: data.paymentMethod,
      },
    }),
};

export interface TicketDetailResponse {
  id: string;
  ticketRef: string;
  ticketCode: string;
  tierName: string;
  tierType: string;
  status: string;
  qrCodeImage: string | null;
  eventName: string;
  eventDate: string;
  venueName: string;
  attendeeName: string;
  isCheckedIn: boolean;
  checkedInAt: string | null;
}

export interface VerifyTicketResponse {
  valid: boolean;
  message: string;
  ticketRef: string | null;
  ticketCode: string | null;
  tierName: string | null;
  tierType: string | null;
  attendeeName: string | null;
  eventName: string | null;
  eventDate: string | null;
  venueName: string | null;
  isCheckedIn: boolean | null;
  checkedInAt: string | null;
}

export const ticketsApi = {
  /* BACKEND: GET /api/v1/events/:id/tickets */
  getByEvent: (eventId: string) =>
    apiFetch<TicketDetailResponse[]>(`/events/${eventId}/tickets`, { method: "GET" }),

  /* BACKEND: POST /api/v1/tickets/:code/verify */
  verify: (code: string) =>
    apiFetch<VerifyTicketResponse>(`/tickets/${code}/verify`, { method: "POST" }),
};

/* ── Room Types ─────────────────────────────────────────────────
   BACKEND ENDPOINTS REQUIRED:
   GET    /api/v1/venues/:id/room-types              → List room types for venue
   POST   /api/v1/venues/:id/room-types             → Create room type
   PUT    /api/v1/venues/:id/room-types/:roomTypeId → Update room type
   DELETE /api/v1/venues/:id/room-types/:roomTypeId  → Remove room type
   ─────────────────────────────────────────────────────────────── */

export interface RoomTypeImageResponse {
  id: string;
  imageUrl: string;
  label?: string;
  sortOrder: number;
}

export interface RoomTypeResponse {
  id: string;
  venueId: string;
  name: string;
  pricePerDay: number;
  capacity: number;
  quantity: number;
  description?: string;
  isActive: boolean;
  images: RoomTypeImageResponse[];
  amenities: string[];
}

export interface RoomTypeImageInput {
  imageUrl: string;
  label?: string;
  sortOrder?: number;
}

export interface CreateRoomTypeRequest {
  name: string;
  pricePerDay: number;
  capacity: number;
  quantity?: number;
  description?: string;
  images?: RoomTypeImageInput[];
  amenities?: string[];
}

export interface UpdateRoomTypeRequest {
  name?: string;
  pricePerDay?: number;
  capacity?: number;
  quantity?: number;
  description?: string;
  isActive?: boolean;
  images?: RoomTypeImageInput[];
  amenities?: string[];
}

export const roomTypesApi = {
  /* BACKEND: GET /api/v1/venues/:venueId/room-types */
  list: (venueId: string) => apiFetch<RoomTypeResponse[]>(`/venues/${venueId}/room-types`),

  /* BACKEND: POST /api/v1/venues/:venueId/room-types */
  create: (venueId: string, data: CreateRoomTypeRequest) =>
    apiFetch<RoomTypeResponse>(`/venues/${venueId}/room-types`, {
      method: "POST",
      body: data,
    }),

  /* BACKEND: PUT /api/v1/venues/:venueId/room-types/:roomTypeId */
  update: (venueId: string, roomTypeId: string, data: UpdateRoomTypeRequest) =>
    apiFetch<RoomTypeResponse>(`/venues/${venueId}/room-types/${roomTypeId}`, {
      method: "PUT",
      body: data,
    }),

  /* BACKEND: DELETE /api/v1/venues/:venueId/room-types/:roomTypeId */
  delete: (venueId: string, roomTypeId: string) =>
    apiFetch<void>(`/venues/${venueId}/room-types/${roomTypeId}`, {
      method: "DELETE",
    }),

  /* BACKEND: PUT /api/v1/venues/:venueId/room-types/:roomTypeId (body: { isActive }) */
  toggleActive: (venueId: string, roomTypeId: string, isActive: boolean) =>
    apiFetch<RoomTypeResponse>(`/venues/${venueId}/room-types/${roomTypeId}`, {
      method: "PUT",
      body: { isActive },
    }),
};

/* ── Admin Dashboard ────────────────────────────────────────────
   BACKEND ENDPOINT REQUIRED:
   GET /api/v1/admin/dashboard → Returns platform KPIs
   (super_admin role required)
   ─────────────────────────────────────────────────────────────── */

export interface DashboardStatsResponse {
  totalVenues: number;
  activeVenues: number;
  totalBookings: number;
  pendingBookings: number;
  confirmedBookings: number;
  cancelledBookings: number;
  totalRevenue: number;
  bookingRevenue: number;
  eventTicketRevenue: number;
  totalUsers: number;
  totalEvents: number;
  upcomingEvents: number;
  totalRefunds: number;
  pendingRefunds: number;
  averageOccupancyRate: number;
  lowOccupancyVenues: number;
}

/* ── Admin Customers ────────────────────────────────────────────── */

export interface CustomerBookingSummary {
  bookingId: string;
  bookingRef: string;
  venueName: string;
  startDate: string;
  endDate: string;
  daysCount: number;
  totalAmount: number;
  status: string;
}

export interface CustomerResponse {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  location?: string;
  createdAt: string;
  bookingCount: number;
  totalSpent: number;
  recentBookings: CustomerBookingSummary[];
}

/* ── Admin Venue Managers ───────────────────────────────────────── */

export interface VenueManagerBookingSummary {
  bookingId: string;
  bookingRef: string;
  guestName: string;
  startDate: string;
  endDate: string;
  daysCount: number;
  totalAmount: number;
  status: string;
}

export interface VenueManagerVenueSummary {
  venueId: string;
  venueName: string;
  location: string;
  bookingCount: number;
  revenue: number;
  recentBookings: VenueManagerBookingSummary[];
}

export interface VenueManagerResponse {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  location?: string;
  businessName?: string;
  businessType?: string;
  cacNumber?: string;
  cacImage?: string;
  ninNumber?: string;
  ninImage?: string;
  hasEventCentre: boolean;
  businessVerificationStatus: "pending" | "approved" | "rejected";
  businessVerifiedAt?: string;
  businessVerificationNote?: string;
  roles: string[];
  createdAt: string;
  venueCount: number;
  totalBookings: number;
  totalRevenue: number;
  venues: VenueManagerVenueSummary[];
}

/* ── Admin Analytics ────────────────────────────────────────────── */

export interface MonthlyRevenueData {
  month: string;
  revenue: number;
}

export interface MonthlyCountData {
  month: string;
  count: number;
}

export interface StatusBreakdown {
  status: string;
  count: number;
  revenue: number;
}

export interface VenuePerformance {
  venueId: string;
  venueName: string;
  bookings: number;
  revenue: number;
}

export interface AnalyticsResponse {
  totalRevenue: number;
  totalBookings: number;
  totalUsers: number;
  totalVenues: number;
  activeVenues: number;
  pendingBookings: number;
  confirmedBookings: number;
  cancelledBookings: number;
  monthlyRevenue: MonthlyRevenueData[];
  monthlyBookings: MonthlyCountData[];
  monthlyUserGrowth: MonthlyCountData[];
  bookingStatusBreakdown: StatusBreakdown[];
  topVenues: VenuePerformance[];
}

export const adminApi = {
  /* BACKEND: GET /api/v1/admin/dashboard — Returns DashboardStatsResponse */
  dashboardStats: () => apiFetch<DashboardStatsResponse>("/admin/dashboard"),

  customers: (params?: { search?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set("search", params.search);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("pageSize", String(params.pageSize));
    const query = qs.toString();
    return apiFetch<PaginatedResponse<CustomerResponse>>(
      `/admin/customers${query ? "?" + query : ""}`,
    );
  },

  venueManagers: (params?: { search?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set("search", params.search);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("pageSize", String(params.pageSize));
    const query = qs.toString();
    return apiFetch<PaginatedResponse<VenueManagerResponse>>(
      `/admin/venue-managers${query ? "?" + query : ""}`,
    );
  },

  updateVenueManagerVerification: (
    managerId: string,
    data: { status: "pending" | "approved" | "rejected"; note?: string },
  ) =>
    apiFetch<{
      managerId: string;
      businessVerificationStatus: "pending" | "approved" | "rejected";
      businessVerifiedAt?: string;
      businessVerificationNote?: string;
    }>(`/admin/venue-managers/${managerId}/verification`, { method: "POST", body: data }),

  analytics: () => apiFetch<AnalyticsResponse>("/admin/analytics"),

  /* BACKEND: GET /api/v1/admin/bookings — All platform bookings */
  bookings: (params?: { status?: string; paymentStatus?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.paymentStatus) qs.set("paymentStatus", params.paymentStatus);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("pageSize", String(params.pageSize));
    const query = qs.toString();
    return apiFetch<PaginatedResponse<{
      id: string;
      bookingRef: string;
      venueName: string;
      guestName: string;
      guestEmail: string;
      startDate: string;
      endDate: string;
      daysCount: number;
      totalAmount: number;
      status: string;
      paymentStatus: string;
      createdAt: string;
    }>>(`/admin/bookings${query ? "?" + query : ""}`);
  },

  /* BACKEND: GET /api/v1/admin/users — All registered users */
  users: (params?: { search?: string; role?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set("search", params.search);
    if (params?.role) qs.set("role", params.role);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("pageSize", String(params.pageSize));
    const query = qs.toString();
    return apiFetch<PaginatedResponse<{
      id: string;
      email?: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      location?: string;
      isVerified: boolean;
      createdAt: string;
      roles: string[];
    }>>(`/admin/users${query ? "?" + query : ""}`);
  },

  /* BACKEND: GET /api/v1/admin/occupancy — Occupancy analytics */
  occupancy: (params?: { fromDate?: string; toDate?: string; venueId?: string }) => {
    const qs = new URLSearchParams();
    if (params?.fromDate) qs.set("fromDate", params.fromDate);
    if (params?.toDate) qs.set("toDate", params.toDate);
    if (params?.venueId) qs.set("venueId", params.venueId);
    const query = qs.toString();
    return apiFetch<{
      periodStart: string;
      periodEnd: string;
      totalVenues: number;
      totalBookingDays: number;
      totalAvailableDays: number;
      occupancyRate: number;
      venueBreakdown: {
        venueId: string;
        venueName: string;
        bookingDays: number;
        availableDays: number;
        occupancyRate: number;
      }[];
    }>(`/admin/occupancy${query ? "?" + query : ""}`);
  },

  /* BACKEND: GET /api/v1/platform-settings — Public platform settings (test mode, min amounts) */
  publicSettings: () =>
    apiFetch<{
      testMode: boolean;
      currency: string;
      currencySymbol: string;
      minVenueBookingAmount: number;
      minWithdrawalAmount: number;
      minTicketTierPrice: number;
    }>("/platform-settings"),

  /* BACKEND: GET /api/v1/admin/settings — Platform settings */
  settings: () =>
    apiFetch<{
      siteName: string;
      supportEmail: string;
      currency: string;
      currencySymbol: string;
      timezone: string;
      maintenanceMode: boolean;
      notifyNewBooking: boolean;
      notifyCancellation: boolean;
      notifyLowOccupancy: boolean;
      lowOccupancyThreshold: number;
      autoApproveVenues: boolean;
      commissionRate: number;
      withdrawalFeeRate: number;
      testMode: boolean;
      minVenueBookingAmount: number;
      minWithdrawalAmount: number;
      minTicketTierPrice: number;
      updatedAt: string;
    }>("/admin/settings"),

  /* BACKEND: PUT /api/v1/admin/settings — Update platform settings */
  updateSettings: (data: {
    siteName?: string;
    supportEmail?: string;
    currency?: string;
    currencySymbol?: string;
    timezone?: string;
    maintenanceMode?: boolean;
    notifyNewBooking?: boolean;
    notifyCancellation?: boolean;
    notifyLowOccupancy?: boolean;
    lowOccupancyThreshold?: number;
    autoApproveVenues?: boolean;
    commissionRate?: number;
    withdrawalFeeRate?: number;
    testMode?: boolean;
    minVenueBookingAmount?: number;
    minWithdrawalAmount?: number;
    minTicketTierPrice?: number;
  }) =>
    apiFetch<{
      message: string;
      updatedAt: string;
    }>("/admin/settings", { method: "PUT", body: data }),

  /* BACKEND: DELETE /api/v1/admin/system/clear — Wipe everything except SuperAdmin */
  clearSystem: () =>
    apiFetch<{
      success: boolean;
      message: string;
      deletedUsers: number;
      deletedVenues: number;
      deletedBookings: number;
      deletedTransactions: number;
      deletedEvents: number;
    }>("/admin/system/clear", { method: "DELETE" }),

  /* BACKEND: POST /api/v1/admin/system/reset-financial-data — Reset all financial data */
  resetFinancialData: () =>
    apiFetch<{ message: string; resetAt: string }>(
      "/admin/system/reset-financial-data",
      { method: "POST" },
    ),

  /* BACKEND: GET /api/v1/admin/system/orphaned-venues — List venues with missing managers */
  orphanedVenues: () =>
    apiFetch<{
      totalVenues: number;
      orphanedCount: number;
      orphanedVenues: {
        venueId: string;
        venueName: string;
        managerId: string | null;
        managerEmail: string | null;
        createdAt: string;
      }[];
    }>("/admin/system/orphaned-venues"),
};

/* ── Manager Dashboard ──────────────────────────────────────────
   BACKEND ENDPOINT REQUIRED:
   GET /api/v1/manager/dashboard?managerId=:id
   ─────────────────────────────────────────────────────────────── */

export interface ManagerVenueSummary {
  id: string;
  name: string;
  type: string;
  location: string;
  status: string;
  moderationStatus: string;
  isAvailable: boolean;
  heroImage: string;
  bookingCount: number;
  revenue: number;
  roomTypeCount: number;
}

export interface ManagerBookingSummary {
  bookingId: string;
  bookingRef: string;
  venueName: string;
  guestName: string;
  startDate: string;
  endDate: string;
  daysCount: number;
  totalAmount: number;
  status: string;
  createdAt: string;
}

export interface ManagerDashboardResponse {
  totalVenues: number;
  totalBookings: number;
  pendingBookings: number;
  confirmedBookings: number;
  totalRevenue: number;
  venueRevenue: number;
  eventRevenue: number;
  totalCommission: number;
  venueCommission: number;
  eventCommission: number;
  netEarnings: number;
  venueNetEarnings: number;
  eventNetEarnings: number;
  earnings30Days: number;
  venueEarnings30Days: number;
  eventEarnings30Days: number;
  bookings30Days: number;
  commissionRate: number;
  venues: ManagerVenueSummary[];
  recentBookings: ManagerBookingSummary[];
}

export const managerApi = {
  dashboard: (managerId: string) =>
    apiFetch<ManagerDashboardResponse>(`/manager/dashboard?managerId=${encodeURIComponent(managerId)}`),

  exportRevenue: (params?: { fromDate?: string; toDate?: string }) => {
    const qs = new URLSearchParams();
    if (params?.fromDate) qs.set("fromDate", params.fromDate);
    if (params?.toDate) qs.set("toDate", params.toDate);
    const query = qs.toString();
    return apiDownload(`/admin/analytics/export/revenue${query ? "?" + query : ""}`, `revenue-export-${new Date().toISOString().slice(0, 10)}.csv`);
  },
};

/* ── Bookings ───────────────────────────────────────────────────
   BACKEND ENDPOINTS REQUIRED:
   POST /api/v1/bookings                → Create new booking (auth)
   PUT  /api/v1/bookings/:id/cancel     → Cancel booking (auth)
   GET  /api/v1/bookings                → List all (admin) or by user
   GET  /api/v1/bookings?userId=:id     → My bookings
   ─────────────────────────────────────────────────────────────── */

export interface BookingRequest {
  venueId: string;
  roomTypeId?: string;
  startDate: string;
  endDate: string;
  guestName: string;
  guestEmail: string;
  totalAmount: number;
  specialRequests?: string;
}

export interface BookingResponse {
  id: string;
  bookingRef: string;
  venueId: string;
  venueName?: string;
  roomTypeId?: string;
  startDate: string;
  endDate: string;
  daysCount: number;
  totalAmount: number;
  status: string;
  paymentStatus: string;
  guestName: string;
  guestEmail: string;
  createdAt: string;
  paymentDeadline?: string;
}

export interface BookingListResponse {
  id: string;
  bookingRef: string;
  guestName?: string;
  guestEmail?: string;
  venueId?: string;
  venueName: string;
  venueImage?: string;
  venueLocation?: string;
  roomTypeName?: string;
  startDate: string;
  endDate: string;
  daysCount: number;
  totalAmount: number;
  pricePerNight?: number;
  status: string;
  paymentStatus: string;
  createdAt: string;
  isExtended?: boolean;
  specialRequests?: string;
  paymentDeadline?: string;
  isEvent?: boolean;
  eventId?: string;
  ticketCode?: string;
  quantity?: number;
  tierName?: string;
}

export const bookingsApi = {
  /* BACKEND: POST /api/v1/bookings — Body: BookingRequest */
  create: (data: BookingRequest) =>
    apiFetch<BookingResponse>("/bookings", { method: "POST", body: data }),

  /* BACKEND: PUT /api/v1/bookings/:id/cancel — Body: { bookingId, reason?, accountName?, accountNumber?, bankName? } */
  cancel: (id: string, data?: { reason?: string; accountName?: string; accountNumber?: string; bankName?: string }) =>
    apiFetch<{ bookingId: string; status: string }>(`/bookings/${id}/cancel`, {
      method: "PUT",
      body: { bookingId: id, reason: data?.reason, accountName: data?.accountName, accountNumber: data?.accountNumber, bankName: data?.bankName },
    }),

  /* BACKEND: GET /api/v1/bookings/:id — Returns BookingListResponse */
  getById: (id: string) => apiFetch<BookingListResponse>(`/bookings/${id}`),

  /* BACKEND: GET /api/v1/bookings — Supports ?status, ?page, ?pageSize */
  list: (params?: { status?: string; page?: number; pageSize?: number }) => {
    const qs = params
      ? "?" +
        new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : "";
    return apiFetch<PaginatedResponse<BookingListResponse>>(`/bookings${qs}`);
  },

  /* BACKEND: GET /api/v1/bookings?userId=:id&pageSize=50 */
  myBookings: (userId: string, status?: string) => {
    const params = new URLSearchParams({ userId, pageSize: "50" });
    if (status) params.set("status", status);
    return apiFetch<PaginatedResponse<BookingListResponse>>(`/bookings?${params.toString()}`);
  },

  /* BACKEND: POST /api/v1/bookings/:id/pay — Process payment (wallet or bank transfer) */
  pay: (id: string, paymentMethod?: "wallet" | "bank_transfer") =>
    apiFetch<{
      bookingId: string;
      bookingRef: string;
      status: string;
      paymentStatus: string;
      amount: number;
      reference: string;
      processedAt: string;
      dedicatedBank: {
        accountNumber: string;
        accountName: string;
        bankName: string;
      } | null;
    }>(`/bookings/${id}/pay`, { method: "POST", body: paymentMethod ? { paymentMethod } : undefined }),

  /* BACKEND: GET /api/v1/bookings/:id/receipt — Returns booking receipt */
  receipt: (id: string) =>
    apiFetch<{
      bookingId: string;
      bookingRef: string;
      guestName: string;
      guestEmail: string;
      venueName: string;
      venueAddress: string;
      roomType: string | null;
      startDate: string;
      endDate: string;
      daysCount: number;
      pricePerDay: number;
      totalAmount: number;
      status: string;
      paymentStatus: string;
      paymentReference: string;
      paidAt: string | null;
      bookedAt: string;
    }>(`/bookings/${id}/receipt`),

  /* BACKEND: POST /api/v1/bookings/cleanup-expired-extensions — Admin only */
  cleanupExpiredExtensions: () =>
    apiFetch<{ revertedCount: number; bookingRefs: string[] }>("/bookings/cleanup-expired-extensions", {
      method: "POST",
    }).catch(() => ({ revertedCount: 0, bookingRefs: [] })),

  /* BACKEND: POST /api/v1/bookings/:id/extend — Extend booking dates */
  extend: (id: string, newEndDate: string, paymentMethod?: "wallet" | "bank_transfer") =>
    apiFetch<{
      bookingId: string;
      bookingRef: string;
      oldEndDate: string;
      newEndDate: string;
      additionalNights: number;
      additionalAmount: number;
      newTotalAmount: number;
      status: string;
      paymentStatus: string;
      message: string;
      dedicatedBank: {
        accountNumber: string;
        accountName: string;
        bankName: string;
      } | null;
    }>(`/bookings/${id}/extend`, { method: "POST", body: { newEndDate, paymentMethod } }),

  /* BACKEND: GET /api/v1/bookings/:id/qr — Get check-in QR token */
  qr: (id: string) =>
    apiFetch<{
      bookingId: string;
      bookingRef: string;
      checkInToken: string;
      guestName: string;
      venueName: string;
      startDate: string;
      endDate: string;
      status: string;
    }>(`/bookings/${id}/qr`),

  /* BACKEND: POST /api/v1/bookings/verify-checkin — Verify QR and mark checked_in */
  verifyCheckIn: (token: string) =>
    apiFetch<{
      bookingId: string;
      bookingRef: string;
      guestName: string;
      venueName: string;
      startDate: string;
      endDate: string;
      previousStatus: string;
      newStatus: string;
      message: string;
      verifiedAt: string;
    }>("/bookings/verify-checkin", { method: "POST", body: { token } }),

  /* BACKEND: GET /api/v1/bookings/stats — User's booking stats */
  stats: () =>
    apiFetch<{
      totalBookings: number;
      pendingBookings: number;
      confirmedBookings: number;
      cancelledBookings: number;
      totalSpent: number;
      recentBookings: {
        id: string;
        bookingRef: string;
        venueName: string;
        roomType: string | null;
        startDate: string;
        endDate: string;
        totalAmount: number;
        status: string;
        paymentStatus: string;
        createdAt: string;
      }[];
    }>("/bookings/stats"),

  /* BACKEND: POST /api/v1/bookings/:id/checkin — Check in guest (manager) */
  checkIn: (id: string) =>
    apiFetch<{
      bookingId: string;
      bookingRef: string;
      guestName: string;
      venueName: string;
      startDate: string;
      endDate: string;
      previousStatus: string;
      newStatus: string;
      message: string;
      checkedInAt: string;
    }>(`/bookings/${id}/checkin`, { method: "POST" }),

  /* BACKEND: POST /api/v1/bookings/:id/approve — Approve booking (manager) */
  approve: (id: string) =>
    apiFetch<{
      bookingId: string;
      bookingRef: string;
      status: string;
      paymentStatus: string;
      message: string;
      approvedAt: string;
    }>(`/bookings/${id}/approve`, { method: "POST" }),

  /* BACKEND: POST /api/v1/bookings/:id/reject — Reject booking (manager) */
  reject: (id: string, reason?: string) =>
    apiFetch<{
      bookingId: string;
      bookingRef: string;
      status: string;
      paymentStatus: string;
      refundAmount: number | null;
      message: string;
      rejectedAt: string;
    }>(`/bookings/${id}/reject`, { method: "POST", body: { reason } }),

  /* BACKEND: POST /api/v1/bookings/:id/checkout — Check out guest (manager) */
  checkout: (id: string) =>
    apiFetch<{
      bookingId: string;
      bookingRef: string;
      guestName: string;
      venueName: string;
      startDate: string;
      endDate: string;
      previousStatus: string;
      newStatus: string;
      message: string;
      checkedOutAt: string;
    }>(`/bookings/${id}/checkout`, { method: "POST" }),

  /* BACKEND: PUT /api/v1/bookings/:id — Update booking dates/details */
  update: (id: string, data: {
    startDate?: string;
    endDate?: string;
    roomTypeId?: string;
    guestName?: string;
    guestEmail?: string;
    specialRequests?: string;
  }) =>
    apiFetch<{
      bookingId: string;
      bookingRef: string;
      startDate: string;
      endDate: string;
      daysCount: number;
      totalAmount: number;
      status: string;
      roomTypeName: string | null;
      updatedAt: string;
    }>(`/bookings/${id}`, { method: "PUT", body: data }),
};

/* ── Reviews ────────────────────────────────────────────────────
   BACKEND ENDPOINTS REQUIRED:
   GET  /api/v1/reviews?venueId=:id → List reviews for venue (public)
   POST /api/v1/venues/:id/reviews   → Submit review (auth)
   ─────────────────────────────────────────────────────────────── */

export interface ReviewResponse {
  id: string;
  venueId: string;
  venueName: string;
  rating: number;
  comment: string;
  isApproved: boolean;
  createdAt: string;
}

export interface CreateReviewRequest {
  venueId: string;
  bookingId?: string;
  rating: number;
  comment: string;
}

export const reviewsApi = {
  /* BACKEND: GET /api/v1/reviews?venueId=:id — Public, returns paginated reviews */
  list: (params?: { venueId?: string; isApproved?: boolean; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.venueId) qs.set("venueId", params.venueId);
    if (params?.isApproved !== undefined) qs.set("isApproved", String(params.isApproved));
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("pageSize", String(params.pageSize));
    const query = qs.toString();
    return apiFetch<PaginatedResponse<ReviewResponse>>(`/reviews${query ? "?" + query : ""}`, {
      auth: false,
    });
  },

  /* BACKEND: POST /api/v1/venues/:venueId/reviews — Body: { rating, comment, bookingId? } */
  create: (venueId: string, data: { rating: number; comment: string; bookingId?: string }) =>
    apiFetch<ReviewResponse>(`/venues/${venueId}/reviews`, { method: "POST", body: data }),

  approve: (id: string) =>
    apiFetch<{ reviewId: string; isApproved: boolean; message: string }>(`/reviews/${id}/approve`, { method: "PUT" }),

  reject: (id: string) =>
    apiFetch<{ reviewId: string; isApproved: boolean; message: string }>(`/reviews/${id}/reject`, { method: "PUT" }),
};

/* ── Support Tickets ─────────────────────────────────────────── */

export interface SupportTicketResponse {
  id: string;
  ticketRef: string;
  guestEmail: string;
  guestName: string;
  subject: string;
  message: string;
  category: string;
  priority: string;
  status: string;
  assignedTo: string | null;
  createdAt: string;
  resolvedAt: string | null;
}

export const supportTicketsApi = {
  create: (data: {
    guestEmail: string;
    guestName: string;
    subject: string;
    message: string;
    category?: string;
    priority?: string;
  }) => apiFetch<{ id: string; ticketRef: string; status: string }>("/support-tickets", { method: "POST", body: data, auth: false }),

  list: (params?: { status?: string; priority?: string; category?: string; search?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.priority) qs.set("priority", params.priority);
    if (params?.category) qs.set("category", params.category);
    if (params?.search) qs.set("search", params.search);
    const query = qs.toString();
    return apiFetch<SupportTicketResponse[]>(`/support-tickets${query ? "?" + query : ""}`);
  },

  updateStatus: (id: string, data: { status: string; resolutionNotes?: string }) =>
    apiFetch<{ id: string; status: string; message: string }>(`/support-tickets/${id}/status`, { method: "PUT", body: data }),
};

/* ── Refunds ─────────────────────────────────────────────────── */

export interface RefundResponse {
  id: string;
  refundRef: string;
  bookingRef: string;
  amount: number;
  reason: string;
  status: string;
  createdAt: string;
  accountName?: string;
  accountNumber?: string;
  bankName?: string;
  userName?: string;
  userEmail?: string;
  userPhone?: string;
}

export const refundsApi = {
  list: (params?: { status?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("pageSize", String(params.pageSize));
    const query = qs.toString();
    return apiFetch<PaginatedResponse<RefundResponse>>(`/refunds${query ? "?" + query : ""}`);
  },

  process: (id: string, data: { status: "approved" | "rejected"; policy?: string; adminNote?: string }) =>
    apiFetch<{ refundId: string; status: string; processedAt: string | null }>(`/refunds/${id}`, { method: "PUT", body: { refundId: id, ...data } }),
};

/* ── Saved Cards ─────────────────────────────────────────────── */

export interface SavedCardResponse {
  id: string;
  cardName: string;
  last4: string;
  cardType: string;
  expiryMonth: string;
  expiryYear: string;
  isDefault: boolean;
}

export const savedCardsApi = {
  list: () => apiFetch<SavedCardResponse[]>("/saved-cards"),
  setDefault: (id: string) => apiFetch<SavedCardResponse>(`/saved-cards/${id}/default`, { method: "PUT" }),
};

/* ── Wishlist ─────────────────────────────────────────────────
   BACKEND ENDPOINTS REQUIRED:
   GET    /api/v1/users/wishlist           → List saved venues (auth)
   POST   /api/v1/users/wishlist           → Add venue (body: { venueId })
   DELETE /api/v1/users/wishlist/:id       → Remove from wishlist
   ─────────────────────────────────────────────────────────────── */

export interface WishlistItemResponse {
  id: string;
  venueId: string;
  venueName: string;
  venueLocation: string;
  venueImage: string | null;
  addedAt: string;
}

export const wishlistApi = {
  /* BACKEND: GET /api/v1/users/wishlist — Returns WishlistItemResponse[] */
  list: () => apiFetch<WishlistItemResponse[]>("/users/wishlist"),

  /* BACKEND: POST /api/v1/users/wishlist — Body: { venueId } */
  add: (venueId: string) =>
    apiFetch<WishlistItemResponse>("/users/wishlist", { method: "POST", body: { venueId } }),

  /* BACKEND: DELETE /api/v1/users/wishlist/:wishlistItemId */
  remove: (wishlistItemId: string) =>
    apiFetch<void>(`/users/wishlist/${wishlistItemId}`, { method: "DELETE" }),
};

/* ── Role Permissions ─────────────────────────────────────── */

const FRONTEND_TO_BACKEND_ROLE: Record<string, string> = {
  super_admin: "SuperAdmin",
  venue_manager: "VenueManager",
  hotel_owner: "HotelOwner",
  event_centre_owner: "EventCentreOwner",
  open_space_owner: "OpenSpaceOwner",
  resort_owner: "ResortOwner",
  hotel_event_owner: "HotelEventOwner",
  apartment_owner: "ApartmentOwner",
  conference_hall_owner: "ConferenceHallOwner",
  moderator: "Moderator",
};

function toBackendRole(role: string) {
  return FRONTEND_TO_BACKEND_ROLE[role] ?? role;
}

export const rolesApi = {
  /* BACKEND: GET /api/v1/roles/:roleName/permissions — Returns string[] */
  getPermissions: (roleName: string) =>
    apiFetch<string[]>(`/roles/${toBackendRole(roleName)}/permissions`),

  /* BACKEND: PUT /api/v1/roles/:roleName/permissions — Body: { roleName, permissions } */
  updatePermissions: (roleName: string, permissions: string[]) => {
    const backendRole = toBackendRole(roleName);
    return apiFetch<void>(`/roles/${backendRole}/permissions`, {
      method: "PUT",  
      body: { roleName: backendRole, permissions },
    });
  },
};

export interface UserProfileResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  dateOfBirth: string;
  location: string;
  profileImage: string | null;
  isVerified: boolean;
  roles: string[];
}

export const usersApi = {
  /* BACKEND: GET /api/v1/users/me — Returns current user profile with roles */
  me: () => apiFetch<UserProfileResponse>("/users/me"),

  /* BACKEND: GET /api/v1/users/me/permissions — Returns string[] */
  getMyPermissions: () => apiFetch<string[]>("/users/me/permissions"),
};

/* ── Wallet & Payments ─────────────────────────────────────── */

export interface WalletResponse {
  balance: number;
  heldBalance: number;
  availableBalance: number;
  dedicatedBank: {
    accountNumber: string;
    accountName: string;
    bankName: string;
  } | null;
  hasPin: boolean;
  withdrawalFeeRate: number;
}

export interface LedgerEntryResponse {
  id: string;
  amount: number;
  previousBalance: number;
  currentBalance: number;
  direction: string;
  description: string | null;
  createdAt: string;
  reference: string | null;
}

export interface WithdrawalResponse {
  id: string;
  amount: number;
  fee: number;
  netAmount: number;
  status: string;
  accountName: string | null;
  accountNumber: string | null;
  bankName: string | null;
  applicantName: string | null;
  remark: string | null;
  updatedAt: string | null;
  createdAt: string;
}

export const walletApi = {
  /* BACKEND: GET /api/v1/wallets/me */
  getWallet: () => apiFetch<WalletResponse>("/wallets/me"),

  /* BACKEND: GET /api/v1/wallets/me/ledger */
  getLedger: () => apiFetch<LedgerEntryResponse[]>("/wallets/me/ledger"),

  /* BACKEND: POST /api/v1/wallets/pin */
  setupPin: (pin: string) => apiFetch<{ message: string }>("/wallets/pin", { method: "POST", body: { pin } }),

  /* BACKEND: PUT /api/v1/wallets/pin */
  updatePin: (oldPin: string, newPin: string) =>
    apiFetch<{ message: string }>("/wallets/pin", { method: "PUT", body: { oldPin, newPin } }),
};

export const withdrawalApi = {
  /* BACKEND: POST /api/v1/withdrawals */
  create: (data: {
    amount: number;
    accountName: string;
    accountNumber: string;
    bankName: string;
    pin: string;
  }) => apiFetch<WithdrawalResponse>("/withdrawals", { method: "POST", body: data }),

  /* BACKEND: GET /api/v1/withdrawals */
  list: (status?: string) => {
    const qs = status ? `?status=${status}` : "";
    return apiFetch<WithdrawalResponse[]>(`/withdrawals${qs}`);
  },

  /* BACKEND: POST /api/v1/withdrawals/:id/approve */
  approve: (id: string, approved: boolean, remark?: string) =>
    apiFetch<WithdrawalResponse>(`/withdrawals/${id}/approve`, { method: "POST", body: { approved, remark } }),
};

export interface PendingEscrowResponse {
  escrowId: string;
  amount: number;
  context: string;
  contextId: string;
  customerName: string;
  customerEmail: string;
  itemName: string | null;
  createdAt: string;
  daysPending: number;
  startDate: string | null;
  endDate: string | null;
  daysCount: number | null;
  roomType: string | null;
  venueImage: string | null;
  totalAmount: number | null;
  checkInToken: string | null;
  commissionRate: number;
  netAmount: number;
}

export const escrowApi = {
  /* BACKEND: GET /api/v1/escrows/pending */
  getPending: () => apiFetch<PendingEscrowResponse[]>("/escrows/pending"),

  /* BACKEND: POST /api/v1/escrows/:id/approve */
  approve: (id: string) => apiFetch<{ escrowId: string; status: string; amount: number }>(`/escrows/${id}/approve`, { method: "POST" }),

  /* BACKEND: POST /api/v1/escrows/:id/reject */
  reject: (id: string, reason?: string) => apiFetch<{ escrowId: string; status: string; amount: number }>(`/escrows/${id}/reject`, { method: "POST", body: { reason } }),
};

/* ── Notifications ──────────────────────────────────────────── */

export interface NotificationDto {
  id: string;
  title: string;
  message: string;
  type: string | null;
  actionUrl: string | null;
  isRead: boolean;
  createdAt: string;
}

export interface NotificationListResponse {
  items: NotificationDto[];
  total: number;
  unreadCount: number;
}

export const notificationsApi = {
  list: (page = 1, pageSize = 20) =>
    apiFetch<NotificationListResponse>(`/notifications?page=${page}&pageSize=${pageSize}`),
  markRead: (id: string) =>
    apiFetch<{ success: boolean; message: string }>(`/notifications/${id}/read`, { method: "POST" }),
};

/* ── Occupancy Analytics ───────────────────────────────────── */

export interface MonthlyRevenue {
  month: string;
  revenue: number;
  bookings: number;
}

export interface PeakDay {
  dayOfWeek: string;
  bookingCount: number;
}

export interface OccupancyAnalyticsResponse {
  venueId: string;
  venueName: string;
  occupancyRate: number;
  totalRevenue: number;
  totalBookings: number;
  cancelledBookings: number;
  checkedInBookings: number;
  revenueByMonth: MonthlyRevenue[];
  peakDays: PeakDay[];
}

export const analyticsApi = {
  getVenueOccupancy: (venueId: string, year?: number) => {
    const qs = year ? `?year=${year}` : "";
    return apiFetch<OccupancyAnalyticsResponse>(`/venues/${venueId}/analytics${qs}`);
  },
};

export const contactApi = {
  send: (data: { name: string; email: string; subject?: string; message: string }) =>
    apiFetch<{ message: string }>("/contact", { method: "POST", body: data, auth: false }),
};

/* ── Uploads (multipart/form-data) ─────────────────────────── */

export interface UploadResponse {
  urls: string[];
}

